import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MapPin } from 'lucide-react';
import LocationPicker from './LocationPicker';

export default function CreateClubModal({ onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = {
      name: formData.name,
      description: formData.description,
      latitude: selectedLocation?.lat || null,
      longitude: selectedLocation?.lng || null,
      formatted_address: selectedLocation?.address || ''
    };

    try {
      await axios.post('/clubs', data);
      toast.success('Club created successfully!');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create club');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!showLocationPicker && (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
          <DialogContent 
            className="sm:max-w-lg"
            data-testid="create-club-modal"
          >
            <DialogHeader>
              <DialogTitle>Create a New Club</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Club Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormField('name', e.target.value)}
                  placeholder="e.g., London Road Cyclists"
                  required
                  data-testid="club-name-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => updateFormField('description', e.target.value)}
                  placeholder="Tell people about your club, riding style, and what to expect..."
                  required
                  rows={3}
                  data-testid="club-description-input"
                />
              </div>

              <div>
                <Label>Club Location (Optional)</Label>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-1 justify-start text-left h-auto py-3"
                  onClick={() => setShowLocationPicker(true)}
                  data-testid="club-location-button"
                >
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0 text-purple-500" />
                  {selectedLocation ? (
                    <span className="truncate text-foreground">{selectedLocation.address}</span>
                  ) : (
                    <span className="text-muted-foreground">Tap to set club location</span>
                  )}
                </Button>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Club'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={selectedLocation}
      />
    </>
  );
}
